<script setup>
import {ref} from 'vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'

const isOpen = ref(false)
const emit = defineEmits(['confirm', 'cancel', 'secondary'])
const props = defineProps({
  title: {
    type: String,
    default: 'Confirm changes',
  },
  description: {
    type: String,
    default: 'Are you sure you want to confirm these changes?',
  },
  confirmText: {
    type: String,
    default: 'Confirm',
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String,
    default: 'default', // 'default' or 'destructive' or 'error'
  },
  secondaryText: {
    type: String,
    default: '',
  },
})

const open = () => {
  isOpen.value = true
}

const cancel = () => {
  isOpen.value = false
  emit('cancel')
}

const confirm = () => {
  isOpen.value = false
  emit('confirm')
}

const secondary = () => {
  isOpen.value = false
  emit('secondary')
}

defineExpose({
  open,
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger as-child v-if="$slots.default">
      <slot />
    </DialogTrigger>
    <DialogContent class="max-w-[350px]">
      <DialogHeader>
        <DialogTitle>{{props.title}}</DialogTitle>
        <DialogDescription>{{props.description}}</DialogDescription>
      </DialogHeader>
      <DialogFooter class="sm:justify-between">
        <Button
          v-if="secondaryText"
          @click="secondary"
          variant="ghost"
          class="mr-auto"
          data-testid="dialog-secondary-button"
        >
          {{ secondaryText }}
        </Button>
        <Button
          v-if="showCancel"
          @click="cancel"
          variant="ghost"
          data-testid="dialog-cancel-button"
        >
          {{ cancelText }}
        </Button>
        <Button
          @click="confirm"
          :variant="variant === 'error' ? 'destructive' : 'default'"
          data-testid="dialog-confirm-button"
        >
          {{ confirmText }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
